<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="real-time-learning-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <h2>Introduction</h2>
            <p>
              In an era where on-demand videos dominate educational technology,
              it's tempting to assume that recorded lessons can replace live
              instruction. Yet the reality is that synchronous
              experiences—whether a live drone tour or an interactive
              lecture—offer unique advantages that static recordings simply
              cannot match. When students know that the next moment could bring
              a new insight or answer their question, their attention sharpens
              and engagement deepens.
            </p>
            <p>
              Pre-recorded content can feel disconnected: it presents
              information but lacks the human element of discovery. By contrast,
              real-time learning brings an instructor (or pilot) and learners
              together in a shared journey—one that adapts, responds, and grows
              organically with each question asked.
            </p>
          </section>

          <section class="blog-section">
            <h2>Immediate Engagement Advantage</h2>
            <p>
              Recorded lessons allow pausing, skipping, or multitasking—and
              students often do just that. In a live session, however, students
              feel the pull of the moment. They know their comment might shape
              the next slide or flight path, and that shared anticipation keeps
              them focused.
            </p>
            <p>
              When a drone streams live over a rainforest canopy, every student
              understands they're witnessing something unique. There's no
              "rewind" safety net—only the thrill of exploration—and that
              urgency drives curiosity far beyond what a fixed video can
              inspire.
            </p>
          </section>

          <section class="blog-section">
            <h2>Interactive Feedback Loops</h2>
            <p>
              One of the strongest benefits of real-time formats is
              instantaneous dialogue. In a pre-recorded video, students must
              note their questions and wait for an answer—if they ever get one.
              Live sessions break down that barrier: a student can ask why a
              river delta fans out in thin channels, and receive a detailed
              explanation on the spot.
            </p>
            <p>
              This back-and-forth fuels critical thinking. As your class probes
              deeper, the instructor pivots, providing clarifications, examples,
              or follow-up questions that guide students toward richer
              understanding. That two-way street transforms a passive
              watch-and-learn model into an active, co-creative process.
            </p>
          </section>

          <section class="blog-section">
            <h2>Adaptive, Personalized Instruction</h2>
            <p>
              No two classrooms behave the same, and every cohort brings its own
              interests and challenges. Live learning lets educators tailor
              content on the fly—slowing down for complex concepts, revisiting a
              feature students find fascinating, or extending discussion when
              engagement peaks.
            </p>
            <p>
              Imagine pausing a live drone tour over a glacier to explain
              crevasse formation, then pivoting to a nearby moraine when
              students request a closer look. Those real-time adjustments aren't
              possible with pre-edited videos, which lock you into a single
              narrative path.
            </p>
          </section>

          <section class="blog-section">
            <h2>Fostering Community and Accountability</h2>
            <p>
              When learning happens in real time, students share the experience.
              They see their peers engage, hear their names called, and feel
              part of a collective exploration. That sense of community boosts
              motivation and reduces the temptation to disengage.
            </p>
            <p>
              Accountability rises, too. In a live environment, students know
              their participation matters—both for their own learning and for
              the rhythm of the session. This shared responsibility creates a
              learning culture that values presence and contribution.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Deepening Connection to Content</h2>
            <p>
              Live experiences bridge the gap between theory and reality.
              Watching a recording of a volcano is informative; witnessing a
              live drone skirt its steaming rim brings home both scale and
              immediacy. Those visceral moments etch lessons into memory.
            </p>
            <p>
              Moreover, real-time learning often sparks spontaneous inquiries
              that lead beyond the lesson plan. A student's question about lava
              flow viscosity might trigger a mini-experiment or further
              research—momentum that a scripted video cannot sustain.
            </p>

            <h2>Conclusion</h2>
            <p>
              Pre-recorded videos have their place—reviewing content, catching
              up on missed sessions, or drilling basic facts. But when the goal
              is to foster engagement, critical thinking, and a genuine
              connection to the material, real-time learning stands alone. Live
              Skyhost drone tours exemplify this power, offering dynamic,
              interactive lessons that resonate long after the stream ends. To
              experience the impact of synchronous exploration, visit
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >
              and schedule your next live session. Elevate your teaching with
              the immediacy and adaptability only real-time learning can
              deliver.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "RealTimeLearningBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "real-time-learning";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .filter((blog) => blog.tags.some((tag) => this.tags.includes(tag)))
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.real-time-learning-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
